import { useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useRef } from 'react';
import { useWatch, type FieldValues } from 'react-hook-form';

import { resolvePatternText } from '@/shared/patterns';
import type { DescriptionItem } from '@/shared/record';

import type { FlowIcon, FlowLeadingStep } from './define-flow';
import type { FlowModel } from './use-flow';

/** Estado de uma etapa no trilho. */
export type FlowTrailState = 'done' | 'current' | 'pending';

/**
 * Uma etapa como o trilho, o painel "Suas escolhas" e o Resumo a mostram (U53). Sai do fluxo (`useFlowTrail`) ou,
 * numa tela de escolha fora do RHF (hub de produto), da própria tela.
 */
export interface FlowTrailStep {
  readonly id: string;
  readonly title: string;
  readonly icon?: FlowIcon | undefined;
  readonly state: FlowTrailState;
  /** O que foi decidido; vazio fica "a definir" (ou "escolhendo agora", na atual). */
  readonly items: readonly DescriptionItem[];
  /** Ir à etapa (trilho e "Alterar"); ausente = não clicável. */
  readonly onSelect?: (() => void) | undefined;
  /** Texto do trilho quando ainda não há valor ("depende do produto"). */
  readonly hint?: string | undefined;
  /** A revisão: fica no trilho, fora do painel. */
  readonly review?: boolean | undefined;
}

/** Atraso do auto-avanço (W07): o ✓ aparece antes de a etapa trocar. */
export const FLOW_AUTO_ADVANCE_MS = 280;

/**
 * Agenda o avanço depois da escolha explícita (W07): uma escolha nova troca a anterior; `cancel` desfaz (troca de
 * etapa antes do prazo) e a saída da tela também.
 */
export function useAutoAdvance() {
  const timer = useRef<number | undefined>(undefined);
  const cancel = useCallback(() => window.clearTimeout(timer.current), []);
  useEffect(() => cancel, [cancel]);
  const schedule = useCallback(
    (run: () => void) => {
      cancel();
      timer.current = window.setTimeout(run, FLOW_AUTO_ADVANCE_MS);
    },
    [cancel],
  );
  return { schedule, cancel };
}

/**
 * O trilho do fluxo: as etapas anteriores (`leading`, concluídas pelo caminho) e as do fluxo, com o estado, o
 * `summary` de cada etapa concluída e o clique de quem já é alcançável (`canGoTo`). Lê com `useWatch`.
 */
export function useFlowTrail<V extends FieldValues>(flow: FlowModel<V>, leading: readonly FlowLeadingStep[] = []): FlowTrailStep[] {
  const { i18n } = useLingui();
  const values = useWatch({ control: flow.form.control }) as V;
  const before = leading.map((step): FlowTrailStep => {
    const title = resolvePatternText(i18n, step.title);
    return { id: step.id, title, icon: step.icon, state: 'done', items: [{ id: step.id, label: title, value: step.value }], onSelect: step.onSelect };
  });
  const own = flow.steps.map((step, index): FlowTrailStep => {
    const done = flow.completed.has(step.id);
    return {
      id: step.id,
      title: resolvePatternText(i18n, step.title),
      icon: step.icon,
      state: index === flow.index ? 'current' : done ? 'done' : 'pending',
      items: done && step.summary ? step.summary(values) : [],
      onSelect: index !== flow.index && flow.canGoTo(step.id) ? () => flow.goTo(step.id) : undefined,
      review: step.kind === 'review',
    };
  });
  return [...before, ...own];
}
